import * as React from "react";
import { useState, useEffect, useRef } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { styled } from "@mui/material/styles";
import TableFooter from "@mui/material/TableFooter";
import { Box, Select, MenuItem, FormControl, Typography, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";

interface ColumnDef<T> {
    /**
     * id is used for identifying a column for sorting etc. Can be omitted if acccessor is a string.
     */
    id?: string;
    label?: string;
    accessor?: keyof T | ((row: T) => any);
    width?: string | number;
    renderCell?: (val: any, row: T) => React.ReactNode;
    disabledSort?: boolean;
}

export interface TableDef<T> {
    columns: ColumnDef<T>[];
    onRowClick?: (row: T) => void;
    sortBy?: {
        id: string;
        desc: boolean;
    };
    rowsPerPage?: number;
}

const sortOrder = {
    ASC: "asc",
    DESC: "desc",
} as const;

type sortDirection = typeof sortOrder[keyof typeof sortOrder];

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
    "&:last-child td, &:last-child th": { border: 0 },
    selected: false,
}));

const defaultRender = (val: any) => {
    return val == null ? "" : String(val);
};

const isSortable = (col: ColumnDef<any>): boolean => {
    return !col.disabledSort && !!col.accessor;
};

/**
 * Used to identify columns for sorting etc.
 */
const getColumnKey = (col: ColumnDef<any>): string | null => {
    if (col.id) {
        return col.id;
    }
    if (typeof col.accessor === "string") {
        return col.accessor;
    }
    return null;
};

const getCellValue = (col: ColumnDef<any>, row: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return typeof col.accessor === "function" ? col.accessor(row) : row[col.accessor];
};

interface Props<T> {
    tableDef: TableDef<T>;
    data: T[];
    disablePagination?: boolean;
    page?: number;
    setPage?: (page: number) => void;
    rowsPerPage?: number;
    setRowsPerPage?: (rows: number) => void;
    totalCount?: number;
}

export const TableWrapper = <T extends object>(props: Props<T>) => {
    const {
        tableDef,
        data,
        disablePagination,
        page: extPage,
        setPage: extSetPage,
        rowsPerPage: extRowsPerPage,
        setRowsPerPage: extSetRowsPerPage,
        totalCount,
    } = props;
    const { columns } = tableDef;

    // Internal state for pagination if not controlled by parent
    const [intPage, intSetPage] = useState<number>(0);
    const [intRowsPerPage, intSetRowsPerPage] = useState<number>(
        !disablePagination ? tableDef.rowsPerPage ?? 26 : data.length
    );

    // Use external state if provided, otherwise internal
    const page = extPage !== undefined ? extPage : intPage;
    const setPage = extSetPage || intSetPage;
    const rowsPerPage = extRowsPerPage !== undefined ? extRowsPerPage : intRowsPerPage;
    const setRowsPerPage = extSetRowsPerPage || intSetRowsPerPage;

    // For server-side pagination, use totalCount; otherwise, use data.length
    const rowCount = totalCount !== undefined ? totalCount : data.length;

    // Sorting state
    const [rows, setRows] = useState<T[]>(data);
    const [sortDirection, setSortDirection] = useState<sortDirection | null>(null);
    const [sortId, setSortId] = useState<string | null>(null);

    useEffect(() => {
        setRows(data);
    }, [data]);

    useEffect(() => {
        if (disablePagination) {
            setRowsPerPage(data.length);
            setPage(0);
        }
    }, [data?.length, disablePagination, setRowsPerPage, setPage]);

    const prevRowsPerPageRef = useRef(rowsPerPage);

    useEffect(() => {
        const newPageCount = Math.max(1, Math.ceil(rowCount / rowsPerPage));
        // If rowsPerPage changed, reset to first page
        if (prevRowsPerPageRef.current !== rowsPerPage) {
            setPage(0);
        }
        // If current page is out of range, set to last valid page
        else if (page > newPageCount - 1) {
            setPage(newPageCount - 1);
        }
        prevRowsPerPageRef.current = rowsPerPage;
    }, [rowsPerPage, rowCount, page, setPage]);

    const handleSort = (col: ColumnDef<any>) => {
        if (!isSortable(col)) return;

        const newSortId = getColumnKey(col);
        const isAsc = newSortId !== sortId || sortDirection === "desc";

        const sortedData = [...rows].sort((a, b) => {
            const aValue = getCellValue(col, a);
            const bValue = getCellValue(col, b);

            if (typeof aValue === "number" && typeof bValue === "number") {
                return isAsc ? aValue - bValue : bValue - aValue;
            }
            const result = Intl.Collator().compare(aValue, bValue);
            return isAsc ? result : -result;
        });

        setRows(sortedData);
        setSortId(newSortId);
        setSortDirection(isAsc ? "asc" : "desc");
    };

    let emptyRows = 0;
    if (rows.length > rowsPerPage) {
        emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);
    }

    // For server-side pagination, data should already be the correct page
    const pagedRows =
        totalCount !== undefined
            ? rows // server-side: data is already paged
            : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <TableContainer component={"div"}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        {columns.map((column, i) => (
                            <TableCell
                                key={column.id ?? i}
                                variant={"head"}
                                sx={{
                                    width: column.width,
                                    "&:hover": {
                                        color: "theme.palette.grey",
                                    }
                                }}
                                className={isSortable(column) ? "cursor-pointer" : ""}
                                onClick={() => handleSort(column)}
                            >
                                {isSortable(column) && sortId && sortDirection && (
                                    <TableSortLabel
                                        active={getColumnKey(column) === sortId}
                                        direction={sortDirection}
                                    />
                                )}
                                {column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {pagedRows.map((row, index) => (
                        <StyledTableRow
                            key={index}
                            onClick={() => tableDef.onRowClick?.(row)}
                        >
                            {columns.map((column, i) => {
                                const val = getCellValue(column, row);
                                return (
                                    <TableCell component="td" scope="cell" key={getColumnKey(column) ?? i}>
                                        {column.renderCell ? column.renderCell(val, row) : defaultRender(val)}
                                    </TableCell>
                                );
                            })}
                        </StyledTableRow>
                    ))}
                    {emptyRows > 0 && (
                        <StyledTableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={columns.length} />
                        </StyledTableRow>
                    )}
                </TableBody>

                {!disablePagination && (
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={columns.length}>
                                <CustomTablePagination
                                    page={page}
                                    pageCount={Math.max(1, Math.ceil(rowCount / rowsPerPage))}
                                    onPageChange={setPage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={setRowsPerPage}
                                />
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                )}
            </Table>
        </TableContainer>
    );
};

export interface CustomTablePaginationProps {
    page: number; // 0-based
    pageCount: number;
    onPageChange: (page: number) => void;
    rowsPerPage: number;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    rowsPerPageOptions?: number[];
}

export const CustomTablePagination: React.FC<CustomTablePaginationProps> = ({
    page,
    pageCount,
    onPageChange,
    rowsPerPage,
    onRowsPerPageChange,
    rowsPerPageOptions = [10, 25, 50, 100],
}) => {
    const { t } = useTranslation();
    return (
        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} px={2} py={1}>
            <IconButton onClick={() => onPageChange(Math.max(page - 1, 0))} disabled={page === 0}>
                <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2">{t("common.GoToPage")}:</Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                    value={page + 1}
                    onChange={e => onPageChange(Number(e.target.value) - 1)}
                >
                    {Array.from({ length: pageCount }, (_, i) => (
                        <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Typography variant="body2">/ {pageCount}</Typography>
            <IconButton onClick={() => onPageChange(Math.min(page + 1, pageCount - 1))} disabled={page >= pageCount - 1}>
                <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2">{t("common.RowsPerPage")}:</Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                    value={rowsPerPage}
                    onChange={e => onRowsPerPageChange(Number(e.target.value))}
                >
                    {rowsPerPageOptions.map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};
